import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Edit2, Plus, RefreshCw } from "lucide-react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Header } from "../components/layout/Header";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

const initialForm = {
  merchant_id: "",
  name: "",
  phone: "",
  bank_name: "",
  bank_account_name: "",
  bank_account_number: "",
  ktp_photo_path: "",
  sort_order: "",
  is_active: true,
};

const modalFieldClass =
  "rounded-sm border-transparent bg-white/[0.04] text-white shadow-none ring-1 ring-white/[0.035] transition-colors focus-visible:ring-white/15";
const modalDialogClass =
  "border-transparent bg-zinc-900 text-white shadow-2xl ring-1 ring-white/[0.04]";
const modalOutlineButtonClass =
  "flex-1 rounded-sm border-transparent bg-white/[0.03] text-white ring-1 ring-white/[0.04] hover:bg-white/[0.06]";
const defaultBankOptions = ["BCA", "BRI", "BNI", "MANDIRI", "SEABANK"];
const monthOptions = [
  { value: "01", label: "Januari" },
  { value: "02", label: "Februari" },
  { value: "03", label: "Maret" },
  { value: "04", label: "April" },
  { value: "05", label: "Mei" },
  { value: "06", label: "Juni" },
  { value: "07", label: "Juli" },
  { value: "08", label: "Agustus" },
  { value: "09", label: "September" },
  { value: "10", label: "Oktober" },
  { value: "11", label: "November" },
  { value: "12", label: "Desember" },
];
const normalizeText = (value) => String(value || "").trim().toLowerCase();
const escapeRegex = (value) => String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const sanitizeScanToken = (value) =>
  String(value || "")
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
    .trim();
const resolveFileUrl = (path) => {
  const value = String(path || "").trim();
  if (!value) return "";
  if (value.startsWith("http://") || value.startsWith("https://")) return value;
  return `${BACKEND_URL}${value}`;
};
const formatDateOnly = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};
const formatDateTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};
const toMonthNumberValue = (value) => {
  const text = String(value || "");
  // Extract YYYY-MM directly from ISO string to avoid timezone drift
  const match = text.match(/^(\d{4})-(\d{2})/);
  if (match) return match[2];
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return "";
  return String(date.getUTCMonth() + 1).padStart(2, "0");
};

const parsePtSessionCount = (descriptionText) => {
  const description = String(descriptionText || "");
  const sessionMatch = description.match(/\((\d+)\s*sesi\)/i);
  if (sessionMatch?.[1]) return Number(sessionMatch[1]);
  const multiplierMatch = description.match(/\b(\d+)\s*x\b/i);
  if (multiplierMatch?.[1]) return Number(multiplierMatch[1]);
  return 1;
};

const buildPtPackageLabel = (descriptionText, trainerName) => {
  const description = String(descriptionText || "").trim();
  const trainer = String(trainerName || "").trim();
  if (!description || !trainer) return description || "-";
  const regex = new RegExp(`^pt\\s*package\\s*-\\s*${escapeRegex(trainer)}\\s*`, "i");
  const cleaned = description.replace(regex, "").trim();
  return cleaned || description;
};

const isPaymentForTrainer = (payment, trainerName) => {
  const description = String(payment?.description || "").trim();
  const trainer = String(trainerName || "").trim();
  if (!description || !trainer) return false;
  const regex = new RegExp(`^pt\\s*package\\s*-\\s*${escapeRegex(trainer)}(?:\\s|$)`, "i");
  return regex.test(description);
};

const normalizePtSessionStatuses = (rawStatuses, totalSessions, legacyStatus) => {
  const total = Math.max(1, Number(totalSessions || 1));
  const statuses = Array.isArray(rawStatuses)
    ? rawStatuses.map((item) => (normalizeText(item) === "done" ? "done" : "not_done"))
    : [];
  const defaultStatus = normalizeText(legacyStatus) === "done" ? "done" : "not_done";
  if (statuses.length === 0) {
    return Array.from({ length: total }, () => defaultStatus);
  }
  const filled = [...statuses];
  while (filled.length < total) {
    filled.push("not_done");
  }
  return filled.slice(0, total);
};

const buildTrainerSummaryFromPackages = (packageRows) => {
  const rows = Array.isArray(packageRows) ? packageRows : [];
  const totalClasses = rows.reduce((sum, item) => sum + Number(item.total_sessions || 0), 0);
  const doneCount = rows.reduce((sum, item) => sum + Number(item.done_count || 0), 0);
  const totalMembers = new Set(rows.map((item) => item.member_key).filter(Boolean)).size;
  return {
    totalClasses,
    totalMembers,
    doneCount,
    notDoneCount: Math.max(totalClasses - doneCount, 0),
  };
};

export default function PtPackages() {
  const { user } = useAuth();
  const isAdmin = String(user?.role || "").trim().toLowerCase() === "admin";
  const [trainers, setTrainers] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isStatsOpen, setIsStatsOpen] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [trainerSessions, setTrainerSessions] = useState([]);
  const [scanBarcode, setScanBarcode] = useState("");
  const [scanLoading, setScanLoading] = useState(false);
  const [regeneratingBarcodePaymentId, setRegeneratingBarcodePaymentId] = useState("");
  const [uploadingKtp, setUploadingKtp] = useState(false);
  const [trainerSummary, setTrainerSummary] = useState({
    totalClasses: 0,
    totalMembers: 0,
    doneCount: 0,
    notDoneCount: 0,
  });
  const [statsMonthFilter, setStatsMonthFilter] = useState("");
  const [selectedPackageActivity, setSelectedPackageActivity] = useState(null);
  const [editingTrainer, setEditingTrainer] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [bankOptions, setBankOptions] = useState(defaultBankOptions);
  const scanInputRef = useRef(null);

  const sortedTrainers = useMemo(
    () =>
      [...trainers].sort((a, b) => {
        const orderA = Number(a.sort_order || 0);
        const orderB = Number(b.sort_order || 0);
        if (orderA !== orderB) return orderA - orderB;
        return String(a.name || "").localeCompare(String(b.name || ""));
      }),
    [trainers]
  );
  const filteredTrainerSessions = useMemo(() => {
    const month = String(statsMonthFilter || "");
    if (!month) return trainerSessions;
    return trainerSessions
      .filter((session) => {
        if (session.activities && session.activities.length > 0) {
          return session.activities.some((a) => {
            if (a.status !== "done") return false;
            return toMonthNumberValue(a.created_at) === month;
          });
        }
        return toMonthNumberValue(session.created_at) === month;
      })
      .map((session) => {
        // Recompute done_count for the selected month only
        if (session.activities && session.activities.length > 0) {
          const monthDone = session.activities.filter(
            (a) => a.status === "done" && toMonthNumberValue(a.created_at) === month
          ).length;
          return {
            ...session,
            done_count: monthDone,
            not_done_count: Math.max(Number(session.total_sessions || 0) - monthDone, 0),
          };
        }
        return session;
      });
  }, [trainerSessions, statsMonthFilter]);
  const filteredTrainerSummary = useMemo(
    () => buildTrainerSummaryFromPackages(filteredTrainerSessions),
    [filteredTrainerSessions]
  );

  useEffect(() => {
    fetchTrainers();
    fetchBankOptions();
    fetchMerchants();
  }, []);

  useEffect(() => {
    if (!isStatsOpen || !isAdmin) return undefined;
    const timer = window.setTimeout(() => scanInputRef.current?.focus(), 80);
    return () => window.clearTimeout(timer);
  }, [isStatsOpen, isAdmin, selectedTrainer?.id]);

  const fetchMerchants = async () => {
    try {
      const { data } = await axios.get(`${API}/merchants`, { withCredentials: true });
      setMerchants(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch merchants:", error);
      setMerchants([]);
    }
  };

  const fetchBankOptions = async () => {
    try {
      const { data } = await axios.get(`${API}/banks`, { withCredentials: true });
      const activeBanks = (Array.isArray(data) ? data : [])
        .filter((item) => item && item.is_active !== false)
        .map((item) => String(item.name || "").trim())
        .filter(Boolean);
      setBankOptions(activeBanks.length > 0 ? activeBanks : defaultBankOptions);
    } catch (error) {
      console.error("Failed to fetch bank options:", error);
      setBankOptions(defaultBankOptions);
    }
  };

  const fetchTrainers = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/pt-trainers`, {
        withCredentials: true,
      });
      setTrainers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch PT trainers:", error);
      alert(error.response?.data?.detail || "Gagal mengambil daftar PT.");
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    if (!isAdmin) return;
    setEditingTrainer(null);
    setFormData({ ...initialForm, merchant_id: merchants[0]?.id || "", is_active: true });
    setIsModalOpen(true);
  };

  const openEdit = (trainer) => {
    if (!isAdmin) return;
    setEditingTrainer(trainer);
    setFormData({
      merchant_id: String(trainer.merchant_id || ""),
      name: String(trainer.name || ""),
      phone: String(trainer.phone || ""),
      bank_name: String(trainer.bank_name || ""),
      bank_account_name: String(trainer.bank_account_name || ""),
      bank_account_number: String(trainer.bank_account_number || ""),
      ktp_photo_path: String(trainer.ktp_photo_path || ""),
      sort_order: String(trainer.sort_order ?? ""),
      is_active: trainer.is_active !== false,
    });
    setIsModalOpen(true);
  };

  const handleKtpUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const body = new FormData();
    body.append("file", file);

    try {
      setUploadingKtp(true);
      const { data } = await axios.post(`${API}/uploads/pt-ktp-image`, body, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFormData((prev) => ({ ...prev, ktp_photo_path: data?.path || "" }));
    } catch (error) {
      console.error("Failed to upload KTP PT:", error);
      alert(error.response?.data?.detail || "Gagal upload foto KTP.");
    } finally {
      setUploadingKtp(false);
      event.target.value = "";
    }
  };

  const fetchTrainerStats = async (trainer) => {
    if (!trainer?.name) return;
    setStatsLoading(true);
    try {
      const params = {};
      if (trainer.merchant_id) params.merchant_id = trainer.merchant_id;
      const { data } = await axios.get(`${API}/payments`, { params, withCredentials: true });
      const payments = Array.isArray(data) ? data : [];
      const ptPackageRows = [];
      payments
        .filter((item) => {
          if (!isPaymentForTrainer(item, trainer.name)) return false;
          // If trainer has merchant_id, also verify payment belongs to same merchant
          if (trainer.merchant_id && item.merchant_id) {
            return item.merchant_id === trainer.merchant_id;
          }
          return true;
        })
        .forEach((item, index) => {
          const classCount = parsePtSessionCount(item.description);
          const normalizedStatuses = normalizePtSessionStatuses(
            item.pt_session_statuses,
            classCount,
            item.session_status
          );
          const memberName = String(item.member_name || "Tanpa Nama").trim() || "Tanpa Nama";
          const memberKey =
            normalizeText(item.member_id) ||
            normalizeText(memberName) ||
            normalizeText(item.transaction_number) ||
            `pt-${index}`;
          const paymentId = String(item.id || `pt-${index}`);
          const packageName = buildPtPackageLabel(item.description, trainer.name);
          const createdAt = String(item.created_at || "");
          const activities = Array.isArray(item.pt_session_activities)
            ? item.pt_session_activities
                .map((activity) => ({
                  id: String(activity?.id || `${paymentId}-${activity?.session_index || 0}-${activity?.created_at || ""}`),
                  session_index: Number(activity?.session_index || 0),
                  status: String(activity?.status || ""),
                  activity: String(activity?.activity || "Sesi PT"),
                  created_at: String(activity?.created_at || ""),
                }))
                .filter((activity) => activity.created_at)
                .sort((a, b) => {
                  const sessionCompare = Number(a.session_index || 0) - Number(b.session_index || 0);
                  if (sessionCompare !== 0) return sessionCompare;
                  return String(a.created_at || "").localeCompare(String(b.created_at || ""));
                })
            : [];
          // Count done from activities (scan date) if available, fallback to statuses
          let doneCount;
          if (activities.length > 0) {
            doneCount = activities.filter((a) => a.status === "done").length;
          } else {
            doneCount = normalizedStatuses.filter((status) => status === "done").length;
          }
          ptPackageRows.push({
            row_key: paymentId,
            payment_id: paymentId,
            member_name: memberName,
            member_key: memberKey,
            package_name: packageName,
            total_sessions: Number(classCount || 0),
            done_count: doneCount,
            not_done_count: Math.max(Number(classCount || 0) - doneCount, 0),
            session_statuses: normalizedStatuses,
            pt_barcode: String(item.pt_barcode || ""),
            activities,
            created_at: createdAt,
          });
        });

      const sortedRows = ptPackageRows.sort((a, b) => {
        const dateCompare = String(b.created_at).localeCompare(String(a.created_at));
        if (dateCompare !== 0) return dateCompare;
        return String(a.package_name || "").localeCompare(String(b.package_name || ""));
      });
      setTrainerSessions(sortedRows);
      setTrainerSummary(buildTrainerSummaryFromPackages(sortedRows));
      setSelectedPackageActivity((current) => {
        if (!current) return null;
        return sortedRows.find((row) => row.row_key === current.row_key) || null;
      });
    } catch (error) {
      console.error("Failed to fetch PT trainer stats:", error);
      alert(error.response?.data?.detail || "Gagal mengambil statistik PT.");
      setTrainerSessions([]);
      setTrainerSummary(buildTrainerSummaryFromPackages([]));
    } finally {
      setStatsLoading(false);
    }
  };

  const submitPtSessionScan = async (rawValue) => {
    if (!isAdmin) {
      alert("Kasir hanya dapat melihat detail Master PT.");
      return;
    }
    const barcode = sanitizeScanToken(rawValue);
    if (!barcode || scanLoading) return;
    const trainerName = String(selectedTrainer?.name || "").trim();
    try {
      setScanLoading(true);
      await axios.post(
        `${API}/payments/pt-session-scan`,
        { barcode, trainer_name: trainerName },
        { withCredentials: true }
      );
      setScanBarcode("");
      await fetchTrainerStats(selectedTrainer);
    } catch (error) {
      console.error("Failed to scan PT barcode:", error);
      alert(error.response?.data?.detail || "Gagal scan barcode sesi PT.");
    } finally {
      setScanLoading(false);
      window.setTimeout(() => scanInputRef.current?.focus(), 50);
    }
  };

  const handleScanBarcode = async (event) => {
    event.preventDefault();
    await submitPtSessionScan(scanBarcode);
  };

  const handleScanBarcodeKeyDown = (event) => {
    const key = String(event.key || "");
    if (key !== "Enter" && key !== "Tab") return;
    event.preventDefault();
    const currentValue = sanitizeScanToken(event.currentTarget.value);
    if (!currentValue || scanLoading) return;
    void submitPtSessionScan(currentValue);
  };

  const handleRegeneratePtBarcode = async (session) => {
    if (!isAdmin) {
      alert("Kasir hanya dapat melihat detail Master PT.");
      return;
    }
    const paymentId = String(session?.payment_id || "").trim();
    if (!paymentId || regeneratingBarcodePaymentId) return;

    try {
      setRegeneratingBarcodePaymentId(paymentId);
      await axios.post(
        `${API}/payments/${paymentId}/package-barcode/regenerate`,
        {},
        { withCredentials: true }
      );
      await fetchTrainerStats(selectedTrainer);
    } catch (error) {
      console.error("Failed to regenerate PT barcode:", error);
      alert(error.response?.data?.detail || "Gagal generate ulang barcode PT.");
    } finally {
      setRegeneratingBarcodePaymentId("");
    }
  };

  const openTrainerStats = async (trainer) => {
    setSelectedTrainer(trainer);
    setIsStatsOpen(true);
    await fetchTrainerStats(trainer);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isAdmin) {
      alert("Kasir hanya dapat melihat detail Master PT.");
      return;
    }

    const payload = {
      merchant_id: String(formData.merchant_id || "").trim(),
      name: String(formData.name || "").trim(),
      phone: String(formData.phone || "").trim(),
      bank_name: String(formData.bank_name || "").trim(),
      bank_account_name: String(formData.bank_account_name || "").trim(),
      bank_account_number: String(formData.bank_account_number || "").trim(),
      ktp_photo_path: String(formData.ktp_photo_path || "").trim(),
      sort_order: Number(formData.sort_order || 0),
      is_active: !!formData.is_active,
    };

    if (!payload.name) {
      alert("Nama PT wajib diisi.");
      return;
    }
    if (!payload.merchant_id) {
      alert("Merchant wajib dipilih.");
      return;
    }

    try {
      if (editingTrainer) {
        await axios.put(`${API}/pt-trainers/${editingTrainer.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/pt-trainers`, payload, { withCredentials: true });
      }
      setIsModalOpen(false);
      setEditingTrainer(null);
      setFormData({ ...initialForm, merchant_id: merchants[0]?.id || "" });
      fetchTrainers();
    } catch (error) {
      console.error("Failed to save PT trainer:", error);
      alert(error.response?.data?.detail || "Gagal menyimpan nama PT.");
    }
  };

  return (
    <DashboardLayout>
      <Header title="PT Menu" />
      <div className="space-y-8 p-8" data-testid="pt-menu-page">
        <section className="rounded-sm border border-white/10 bg-zinc-900/50 p-6">
          <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-white">Daftar Nama PT</h2>
              <p className="mt-1 text-sm text-zinc-500">
                {isAdmin
                  ? "Menu ini khusus untuk input nama PT yang bisa dipilih pengunjung saat order paket PT."
                  : "Mode kasir: hanya melihat detail Master PT."}
              </p>
            </div>
            {isAdmin && (
              <Button
                type="button"
                onClick={openCreate}
                className="rounded-sm bg-[#D4AF37] font-semibold text-black hover:bg-[#C59A27]"
              >
                <Plus className="mr-2 h-4 w-4" strokeWidth={2} />
                Tambah Nama PT
              </Button>
            )}
          </div>

          <div className="overflow-hidden rounded-sm border border-white/10 bg-black/20">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead>No</TableHead>
                  <TableHead>Nama PT</TableHead>
                  <TableHead>Merchant</TableHead>
                  <TableHead>No HP</TableHead>
                  <TableHead>Rekening</TableHead>
                  <TableHead>Foto KTP</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-8 text-center">
                      <div className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-[#D4AF37] border-t-transparent" />
                    </TableCell>
                  </TableRow>
                ) : sortedTrainers.length > 0 ? (
                  sortedTrainers.map((item, index) => (
                    <TableRow key={item.id || index} className="border-white/5 hover:bg-zinc-900/50 transition-colors">
                    <TableCell className="py-4 text-sm text-zinc-300">{index + 1}</TableCell>
                    <TableCell className="py-4 text-sm font-medium text-white">
                      <button
                          type="button"
                          onClick={() => openTrainerStats(item)}
                          className="rounded-sm text-left text-[#F3D77A] underline-offset-4 transition hover:text-[#D4AF37] hover:underline"
                        >
                          {item.name}
                      </button>
                    </TableCell>
                    <TableCell className="py-4 text-sm text-zinc-300">{item.merchant_name || "-"}</TableCell>
                    <TableCell className="py-4 text-sm text-zinc-300">{item.phone || "-"}</TableCell>
                      <TableCell className="py-4 text-sm text-zinc-300">
                        {item.bank_name || item.bank_account_name || item.bank_account_number ? (
                          <div className="space-y-1">
                            <p className="font-semibold text-zinc-100">
                              {item.bank_account_name || item.bank_account_number || "-"}
                            </p>
                            <p className="text-xs text-zinc-500">
                              {item.bank_name || "-"}
                              {item.bank_account_name && item.bank_account_number ? ` - ${item.bank_account_number}` : ""}
                            </p>
                          </div>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell className="py-4 text-sm">
                        {item.ktp_photo_path ? (
                          <a
                            href={resolveFileUrl(item.ktp_photo_path)}
                            target="_blank"
                            rel="noreferrer"
                            className="font-medium text-sky-300 hover:text-sky-200"
                          >
                            Lihat KTP
                          </a>
                        ) : (
                          <span className="text-zinc-500">-</span>
                        )}
                      </TableCell>
                      <TableCell className="py-4">
                        <Badge
                          variant="outline"
                          className={`rounded-sm text-xs ${
                            item.is_active !== false
                              ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
                              : "border-zinc-500/30 bg-zinc-500/10 text-zinc-400"
                          }`}
                        >
                          {item.is_active !== false ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {isAdmin ? (
                            <button
                              type="button"
                              onClick={() => openEdit(item)}
                              className="rounded-sm p-2 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-white"
                            >
                              <Edit2 className="h-4 w-4" strokeWidth={1.5} />
                            </button>
                          ) : (
                            <span className="text-xs text-zinc-500">View only</span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} className="py-8 text-center text-sm text-zinc-500">
                      Belum ada nama PT.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </section>

        <Dialog
          open={isStatsOpen}
          onOpenChange={(open) => {
            setIsStatsOpen(open);
            if (!open) {
              setSelectedTrainer(null);
              setTrainerSessions([]);
              setScanBarcode("");
              setStatsMonthFilter("");
              setSelectedPackageActivity(null);
              setTrainerSummary({
                totalClasses: 0,
                totalMembers: 0,
                doneCount: 0,
                notDoneCount: 0,
              });
            }
          }}
        >
          <DialogContent className="max-h-[92vh] w-[min(1180px,calc(100vw-32px))] max-w-none overflow-y-auto border-transparent bg-zinc-900 p-5 text-white shadow-2xl ring-1 ring-white/[0.04]">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold">
                Statistik PT - {selectedTrainer?.name || "-"}
              </DialogTitle>
            </DialogHeader>
            {statsLoading ? (
              <div className="py-10 text-center">
                <div className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-[#D4AF37] border-t-transparent" />
              </div>
            ) : (
              <div className="space-y-3 pr-1">
                {isAdmin ? (
                  <form onSubmit={handleScanBarcode} className="rounded-sm border border-[#D4AF37]/20 bg-[#D4AF37]/5 p-3">
                    <div className="flex flex-col gap-2 lg:flex-row lg:items-end">
                      <div className="flex-1 space-y-1">
                        <Label className="text-[11px] uppercase tracking-[0.14em] text-[#F3D77A]">Scan Barcode Sesi PT</Label>
                        <Input
                          ref={scanInputRef}
                          value={scanBarcode}
                          onChange={(event) => setScanBarcode(sanitizeScanToken(event.target.value))}
                          onKeyDown={handleScanBarcodeKeyDown}
                          className={modalFieldClass}
                          placeholder="Scan barcode PT / QR member di sini"
                          autoComplete="off"
                        />
                        <p className="text-[11px] text-zinc-500">
                          Scan akan otomatis mengubah 1 sesi berikutnya menjadi Done.
                        </p>
                      </div>
                      <Button
                        type="submit"
                        disabled={scanLoading || !scanBarcode.trim()}
                        className="rounded-sm bg-[#D4AF37] font-semibold text-black hover:bg-[#C59A27]"
                      >
                        {scanLoading ? "Memproses..." : "Scan"}
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div className="rounded-sm border border-white/10 bg-black/20 px-3 py-2 text-xs text-zinc-400">
                    Mode kasir hanya dapat melihat detail sesi PT.
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
                  <div className="rounded-sm border border-white/10 bg-black/20 p-3">
                    <p className="text-[11px] uppercase tracking-[0.14em] text-zinc-500">Total Kelas</p>
                    <p className="mt-1 text-lg font-semibold text-white">{filteredTrainerSummary.totalClasses}</p>
                  </div>
                  <div className="rounded-sm border border-white/10 bg-black/20 p-3">
                    <p className="text-[11px] uppercase tracking-[0.14em] text-zinc-500">Total Member</p>
                    <p className="mt-1 text-lg font-semibold text-white">{filteredTrainerSummary.totalMembers}</p>
                  </div>
                  <div className="rounded-sm border border-emerald-500/20 bg-emerald-500/10 p-3">
                    <p className="text-[11px] uppercase tracking-[0.14em] text-emerald-300">Done</p>
                    <p className="mt-1 text-lg font-semibold text-emerald-300">{filteredTrainerSummary.doneCount}</p>
                  </div>
                  <div className="rounded-sm border border-rose-500/20 bg-rose-500/10 p-3">
                    <p className="text-[11px] uppercase tracking-[0.14em] text-rose-300">Not Done</p>
                    <p className="mt-1 text-lg font-semibold text-rose-300">{filteredTrainerSummary.notDoneCount}</p>
                  </div>
                </div>

                <div className="overflow-hidden rounded-sm border border-white/10 bg-black/20">
                  <div className="flex flex-col gap-3 border-b border-white/10 px-3 py-3 md:flex-row md:items-end md:justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500">Detail Paket PT</p>
                      <p className="mt-1 text-[11px] text-zinc-600">Filter berdasarkan bulan member daftar paket PT.</p>
                    </div>
                    <div className="grid gap-2 md:grid-cols-[180px_auto]">
                      <div className="space-y-1">
                        <Label className="text-[10px] uppercase tracking-[0.14em] text-zinc-500">Bulan Daftar</Label>
                        <Select
                          value={statsMonthFilter || "all"}
                          onValueChange={(value) => setStatsMonthFilter(value === "all" ? "" : value)}
                        >
                          <SelectTrigger className={modalFieldClass}>
                            <SelectValue placeholder="Pilih bulan" />
                          </SelectTrigger>
                          <SelectContent className="border-white/10 bg-zinc-950 text-white">
                            <SelectItem value="all">Semua Bulan</SelectItem>
                            {monthOptions.map((month) => (
                              <SelectItem key={month.value} value={month.value}>
                                {month.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setStatsMonthFilter("")}
                        className="self-end rounded-sm border-white/10 text-white hover:bg-zinc-800"
                      >
                        Reset
                      </Button>
                    </div>
                  </div>
                  <div className="max-h-[46vh] overflow-auto">
                  <Table className="min-w-[980px]">
                    <TableHeader>
                      <TableRow className="border-white/10 hover:bg-transparent">
                        <TableHead>No</TableHead>
                        <TableHead>Member</TableHead>
                        <TableHead>Paket PT</TableHead>
                        <TableHead>Total Sesi</TableHead>
                        <TableHead>Done</TableHead>
                        <TableHead>Not Done</TableHead>
                        <TableHead>Barcode</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Tanggal</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredTrainerSessions.length > 0 ? (
                        filteredTrainerSessions.map((session, index) => (
                          <TableRow key={session.row_key || index} className="border-white/5 hover:bg-zinc-900/50 transition-colors">
                            <TableCell className="py-3 text-sm text-zinc-300">{index + 1}</TableCell>
                            <TableCell className="py-3 text-sm">
                              <button
                                type="button"
                                onClick={() => setSelectedPackageActivity(session)}
                                className="text-left text-[#F3D77A] underline-offset-4 transition hover:text-[#D4AF37] hover:underline"
                              >
                                {session.member_name}
                              </button>
                            </TableCell>
                            <TableCell className="py-3 text-sm text-zinc-100">{session.package_name}</TableCell>
                            <TableCell className="py-3 text-sm text-zinc-300">{session.total_sessions}</TableCell>
                            <TableCell className="py-3 text-sm text-emerald-300">{session.done_count}</TableCell>
                            <TableCell className="py-3 text-sm text-rose-300">{session.not_done_count}</TableCell>
                            <TableCell className="py-3">
                              <div className="space-y-2">
                                {session.pt_barcode ? (
                                  <div className="flex items-center gap-2">
                                    <div className="rounded-sm bg-white p-1">
                                      <img
                                        src={`${API}/payments/${session.payment_id}/pt-barcode.svg`}
                                        alt={`Barcode ${session.pt_barcode}`}
                                        className="h-7 w-24 object-contain"
                                      />
                                    </div>
                                    <p className="whitespace-nowrap font-mono text-[10px] font-semibold text-zinc-400">{session.pt_barcode}</p>
                                  </div>
                                ) : (
                                  <span className="text-xs text-zinc-500">-</span>
                                )}
                                {isAdmin ? (
                                  <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => handleRegeneratePtBarcode(session)}
                                    disabled={Boolean(regeneratingBarcodePaymentId)}
                                    className="h-7 rounded-sm border-white/10 bg-white/[0.03] px-2 text-[10px] text-white hover:bg-zinc-800"
                                  >
                                    <RefreshCw
                                      className={`mr-1 h-3 w-3 ${regeneratingBarcodePaymentId === session.payment_id ? "animate-spin" : ""}`}
                                    />
                                    {regeneratingBarcodePaymentId === session.payment_id ? "Memproses..." : "Generate Ulang"}
                                  </Button>
                                ) : null}
                              </div>
                            </TableCell>
                            <TableCell className="py-3">
                              <div className="flex flex-col gap-1">
                                {(() => {
                                  const isExpired = session.done_count >= session.total_sessions && session.total_sessions > 0;
                                  return (
                                <span
                                  className={`inline-flex w-fit items-center rounded-sm border px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] ${
                                    isExpired
                                      ? "border-zinc-500/30 bg-zinc-500/10 text-zinc-300"
                                      : "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                                  }`}
                                >
                                  {isExpired ? "Expired" : "Active"}
                                </span>
                                  );
                                })()}
                              </div>
                            </TableCell>
                            <TableCell className="py-3 text-sm text-zinc-400">{formatDateOnly(session.created_at)}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={9} className="py-8 text-center text-sm text-zinc-500">
                            Belum ada pembayaran paket PT untuk filter ini.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  </div>
                </div>
                {selectedPackageActivity ? (
                  <div className="rounded-sm border border-white/10 bg-black/20">
                    <div className="flex items-start justify-between gap-4 border-b border-white/10 px-3 py-3">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500">Riwayat Scan Member</p>
                        <h4 className="mt-1 text-sm font-semibold text-white">
                          {selectedPackageActivity.member_name} - {selectedPackageActivity.package_name}
                        </h4>
                        <p className="mt-1 text-xs text-zinc-500">
                          Daftar paket: {formatDateTime(selectedPackageActivity.created_at)}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setSelectedPackageActivity(null)}
                        className="rounded-sm border-white/10 text-white hover:bg-zinc-800"
                      >
                        Tutup
                      </Button>
                    </div>
                    <div className="max-h-40 overflow-y-auto divide-y divide-white/5">
                      {(selectedPackageActivity.activities || []).length > 0 ? (
                        selectedPackageActivity.activities.map((activity) => (
                          <div key={activity.id} className="grid gap-2 px-3 py-2.5 text-sm md:grid-cols-[90px_1fr_150px] md:items-center">
                            <div>
                              <Badge className="rounded-sm border-emerald-500/30 bg-emerald-500/10 text-emerald-300">
                                Sesi {Number(activity.session_index || 0) + 1}
                              </Badge>
                            </div>
                            <p className="text-zinc-300">{activity.activity}</p>
                            <p className="text-xs text-zinc-500 md:text-right">{formatDateTime(activity.created_at)}</p>
                          </div>
                        ))
                      ) : (
                        <div className="px-3 py-5 text-center text-sm text-zinc-500">
                          Belum ada activity scan untuk member ini. Data akan muncul setelah member scan barcode.
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog
          open={isModalOpen}
          onOpenChange={(open) => {
            setIsModalOpen(open);
            if (!open) setEditingTrainer(null);
          }}
        >
          <DialogContent className={`max-w-2xl ${modalDialogClass}`}>
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold">
                {editingTrainer ? "Edit Nama PT" : "Tambah Nama PT"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="mt-4 space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Merchant</Label>
                  <Select
                    value={formData.merchant_id || undefined}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, merchant_id: value }))}
                    disabled={!isAdmin}
                  >
                    <SelectTrigger className={modalFieldClass}>
                      <SelectValue placeholder="Pilih merchant" />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-zinc-950 text-white">
                      {merchants.map((merchant) => (
                        <SelectItem key={merchant.id} value={merchant.id}>
                          {merchant.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {isAdmin && merchants.length === 0 ? (
                    <p className="text-xs text-amber-400">Tambahkan Merchant dulu sebelum membuat Master PT.</p>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Nama PT</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                    className={modalFieldClass}
                    placeholder="Contoh: Ben"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">No HP</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                    className={modalFieldClass}
                    placeholder="Contoh: 08123456789"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Tipe Bank</Label>
                  <Select
                    value={formData.bank_name || undefined}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, bank_name: value }))}
                    disabled
                  >
                    <SelectTrigger className={modalFieldClass}>
                      <SelectValue placeholder="Pilih bank" />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-zinc-950 text-white">
                      {[...new Set([formData.bank_name, ...bankOptions].filter(Boolean))].map((bank) => (
                        <SelectItem key={bank} value={bank}>{bank}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Account Name</Label>
                  <Input
                    value={formData.bank_account_name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, bank_account_name: e.target.value }))}
                    className={modalFieldClass}
                    placeholder="Nama sesuai rekening"
                    disabled
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">No Rekening</Label>
                  <Input
                    value={formData.bank_account_number}
                    onChange={(e) => setFormData((prev) => ({ ...prev, bank_account_number: e.target.value }))}
                    className={modalFieldClass}
                    placeholder="Nomor rekening PT"
                    disabled
                  />
                </div>
              </div>
              <p className="rounded-sm border border-[#D4AF37]/20 bg-[#D4AF37]/5 px-3 py-2 text-xs text-[#F3D77A]">
                Data rekening hanya bisa diubah dari menu Master Rekening.
              </p>
              <div className="space-y-2 rounded-sm border border-white/10 bg-black/20 p-3">
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Foto KTP</Label>
                <div className="flex flex-col gap-3 md:flex-row md:items-center">
                  <Input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={handleKtpUpload}
                    className={modalFieldClass}
                    disabled={uploadingKtp}
                  />
                  {formData.ktp_photo_path ? (
                    <a
                      href={resolveFileUrl(formData.ktp_photo_path)}
                      target="_blank"
                      rel="noreferrer"
                      className="whitespace-nowrap text-sm font-medium text-sky-300 hover:text-sky-200"
                    >
                      Lihat Foto KTP
                    </a>
                  ) : null}
                </div>
                {uploadingKtp ? <p className="text-xs text-zinc-500">Mengupload foto KTP...</p> : null}
                {formData.ktp_photo_path ? (
                  <button
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, ktp_photo_path: "" }))}
                    className="text-xs text-rose-300 hover:text-rose-200"
                  >
                    Hapus lampiran KTP
                  </button>
                ) : null}
              </div>
              <div className="grid grid-cols-1 items-center gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Sort Order</Label>
                  <Input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData((prev) => ({ ...prev, sort_order: e.target.value }))}
                    className={modalFieldClass}
                  />
                </div>
                <div className="flex items-center justify-between pt-5 md:justify-start md:gap-3">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Active</Label>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, is_active: checked }))}
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                  className={modalOutlineButtonClass}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!isAdmin || merchants.length === 0}
                  className="flex-1 rounded-sm bg-[#D4AF37] font-semibold text-black hover:bg-[#C59A27]"
                >
                  {editingTrainer ? "Update Nama PT" : "Tambah Nama PT"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
